/**
 * Tarifs — 4 plans + toggle mensuel/annuel + switch devise (auto-détectée).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { colors, fonts, goldGradient } from "../../styles/tokens";
import Button from "../Button";
import SwitchDevise from "../SwitchDevise";
import useScrollFadeIn from "../../hooks/useScrollFadeIn";
import useDevise from "../../hooks/useDevise";
import { isAfricanDevise } from "../../config/devises";

const PLANS = [
  {
    key: "freemium",
    nom: "FREEMIUM",
    free: true,
    cta: "Commencer gratuitement",
    features: [
      { ok: true,  text: "75 membres max" },
      { ok: true,  text: "1 église" },
      { ok: true,  text: "Annuaire + Présences + Événements" },
      { ok: false, text: "Gestion financière" },
      { ok: false, text: "Exports" },
    ],
  },
  {
    key: "communaute",
    nom: "COMMUNAUTÉ",
    cta: "Choisir Communauté",
    features: [
      { ok: true, text: "150 membres · 1 église" },
      { ok: true, text: "Tout Freemium + Dons + Quêtes" },
      { ok: true, text: "Export PDF basique" },
      { ok: true, text: "3 utilisateurs" },
    ],
  },
  {
    key: "starter",
    nom: "STARTER",
    cta: "Choisir Starter",
    populaire: true,
    features: [
      { ok: true, text: "500 membres · 3 églises" },
      { ok: true, text: "Dashboard complet + Statistiques" },
      { ok: true, text: "Export Excel/CSV/PDF" },
      { ok: true, text: "Bénévoles + Planning" },
      { ok: true, text: "10 utilisateurs" },
      { ok: true, text: "Support prioritaire" },
    ],
  },
  {
    key: "pro",
    nom: "PRO",
    cta: "Contacter pour Pro",
    features: [
      { ok: true, text: "Membres illimités" },
      { ok: true, text: "Réseau multi-églises illimité" },
      { ok: true, text: "Tous les add-ons inclus" },
      { ok: true, text: "Utilisateurs illimités" },
      { ok: true, text: "API access" },
      { ok: true, text: "Support dédié" },
    ],
  },
];

export default function Tarifs() {
  const [annuel, setAnnuel] = useState(true);
  const [titleRef, titleStyle] = useScrollFadeIn();
  const { devise, pays, drapeau, prix, afficher } = useDevise();
  const cycle = annuel ? "annuel" : "mensuel";

  return (
    <section
      id="tarifs"
      style={{
        padding: "clamp(64px, 10vw, 100px) clamp(16px, 4vw, 24px)",
        background: colors.bg,
        fontFamily: fonts.body,
        maxWidth: "100vw",
        overflowX: "hidden",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div ref={titleRef} style={{ ...titleStyle, textAlign: "center", marginBottom: "32px" }}>
          <h2 style={{
            fontFamily: fonts.heading,
            fontSize: "clamp(32px, 4vw, 48px)",
            fontWeight: 700,
            color: colors.text,
            marginBottom: "12px",
            letterSpacing: "-0.01em",
          }}>
            Commencez gratuitement.<br />
            <span style={{ color: colors.gold }}>Grandissez à votre rythme.</span>
          </h2>
          {pays && (
            <p style={{ color: colors.textMuted, fontSize: 13, marginTop: 4 }}>
              🌍 {pays} détecté{isAfricanDevise(devise) ? "e" : ""} · Prix en {devise === "EUR" ? "EUR" : devise === "FCFA" ? "FCFA" : devise}
            </p>
          )}
        </div>

        {/* Toggle + SwitchDevise */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: "48px",
        }}>
          <div style={{
            display: "inline-flex",
            background: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: "999px",
            padding: "4px",
          }}>
            <button
              onClick={() => setAnnuel(false)}
              style={{
                padding: "10px 22px",
                borderRadius: "999px",
                fontSize: "14px",
                fontWeight: 600,
                color: !annuel ? colors.bg : colors.textMuted,
                background: !annuel ? goldGradient : "transparent",
                transition: "all 200ms",
              }}
            >
              Mensuel
            </button>
            <button
              onClick={() => setAnnuel(true)}
              style={{
                padding: "10px 22px",
                borderRadius: "999px",
                fontSize: "14px",
                fontWeight: 600,
                color: annuel ? colors.bg : colors.textMuted,
                background: annuel ? goldGradient : "transparent",
                transition: "all 200ms",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              Annuel <span style={{ fontSize: "11px", opacity: 0.85 }}>🙏 -10%</span>
            </button>
          </div>
          <SwitchDevise />
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "16px",
          alignItems: "stretch",
        }}>
          {PLANS.map((plan, i) => (
            <PlanCard
              key={plan.nom}
              plan={plan}
              annuel={annuel}
              prixMontant={plan.free ? 0 : prix(plan.key, cycle)}
              afficher={afficher}
              delay={i * 80}
            />
          ))}
        </div>

        <p style={{
          marginTop: "32px",
          textAlign: "center",
          color: colors.textMuted,
          fontSize: "13px",
          lineHeight: 1.6,
        }}>
          💡 Tarifs disponibles en FCFA, CAD et CHF · Mobile Money bientôt disponible · Sans engagement · Annulez à tout moment
        </p>
      </div>
    </section>
  );
}

function PlanCard({ plan, annuel, prixMontant, afficher, delay }) {
  const [ref, style] = useScrollFadeIn({ delay });
  const prix = prixMontant ?? 0;
  const populaire = plan.populaire;

  return (
    <div ref={ref} style={{
      ...style,
      position: "relative",
      padding: "28px 22px",
      background: populaire ? `linear-gradient(180deg, ${colors.goldDim} 0%, ${colors.card} 100%)` : colors.card,
      border: `1px solid ${populaire ? colors.gold : colors.border}`,
      borderRadius: "16px",
      display: "flex",
      flexDirection: "column",
      boxShadow: populaire ? "0 12px 40px rgba(228,167,44,0.15)" : "none",
    }}>
      {populaire && (
        <div style={{
          position: "absolute",
          top: -12,
          left: "50%",
          transform: "translateX(-50%)",
          padding: "4px 14px",
          background: goldGradient,
          color: colors.bg,
          fontSize: "11px",
          fontWeight: 700,
          borderRadius: "999px",
          letterSpacing: "0.05em",
          whiteSpace: "nowrap",
        }}>
          ★ Le plus populaire
        </div>
      )}

      <div style={{ fontSize: "12px", letterSpacing: "0.1em", color: colors.gold, fontWeight: 700, marginBottom: "8px" }}>
        {plan.nom}
      </div>

      <div style={{ marginBottom: "20px" }}>
        <span style={{
          fontFamily: fonts.heading,
          fontSize: "44px",
          fontWeight: 700,
          color: colors.text,
          letterSpacing: "-0.02em",
          transition: "all 250ms",
        }}>
          {prix === 0 ? "0" : (afficher ? afficher(prix) : prix)}
        </span>
        {prix > 0 && (
          <span style={{ color: colors.textMuted, fontSize: "13px", marginLeft: "6px" }}>
            {annuel ? "/an" : "/mois"}
          </span>
        )}
      </div>

      <ul style={{ listStyle: "none", padding: 0, margin: 0, flex: 1, marginBottom: "20px" }}>
        {plan.features.map((f) => (
          <li key={f.text} style={{
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
            padding: "6px 0",
            fontSize: "13px",
            color: f.ok ? colors.text : colors.textDim,
            lineHeight: 1.5,
          }}>
            <span style={{
              color: f.ok ? "#2DD4A0" : colors.rose,
              flexShrink: 0,
              fontWeight: 700,
            }}>
              {f.ok ? "✓" : "✗"}
            </span>
            {f.text}
          </li>
        ))}
      </ul>

      <Link to="/inscription" style={{ textDecoration: "none" }}>
        <Button variant={populaire ? "primary" : "ghost"} fullWidth>
          {plan.cta}
        </Button>
      </Link>

      {prix > 0 && <PaymentBadges />}
    </div>
  );
}

// ─── Logos SVG inline officiels ───
const LOGO_HEIGHT = 24;

const VisaLogo = () => (
  <svg viewBox="0 0 48 16" width="48" height={LOGO_HEIGHT} aria-label="Visa">
    <text x="0" y="14" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="16" fill="#1A1F71">VISA</text>
  </svg>
);

const MastercardLogo = () => (
  <svg viewBox="0 0 38 24" width="38" height={LOGO_HEIGHT} aria-label="Mastercard">
    <circle cx="13" cy="12" r="10" fill="#EB001B" />
    <circle cx="25" cy="12" r="10" fill="#F79E1B" />
    <path d="M19 5.4a10 10 0 0 1 0 13.2A10 10 0 0 1 19 5.4z" fill="#FF5F00" />
  </svg>
);

const PayPalLogo = () => (
  <svg viewBox="0 0 60 20" width="60" height={LOGO_HEIGHT} aria-label="PayPal">
    <text x="0"  y="16" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="16" fill="#003087">Pay</text>
    <text x="30" y="16" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="16" fill="#009CDE">Pal</text>
  </svg>
);

const OrangeMoneyLogo = () => (
  <svg viewBox="0 0 40 24" width="40" height={LOGO_HEIGHT} aria-label="Orange Money">
    <rect width="40" height="24" rx="4" fill="#FF6600" />
    <text x="4" y="17" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="13" fill="white">OM</text>
  </svg>
);

const WaveLogo = () => (
  <svg viewBox="0 0 50 24" width="50" height={LOGO_HEIGHT} aria-label="Wave">
    <rect width="50" height="24" rx="4" fill="#1E88E5" />
    <text x="6" y="17" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="13" fill="white">Wave</text>
  </svg>
);

const MtnMomoLogo = () => (
  <svg viewBox="0 0 50 24" width="50" height={LOGO_HEIGHT} aria-label="MTN MoMo">
    <rect width="50" height="24" rx="4" fill="#FFCB00" />
    <text x="5" y="17" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="12" fill="#000">MoMo</text>
  </svg>
);

// Carte bancaires sur fond blanc, mobile money avec leur fond intégré au SVG.
const PAYMENT_METHODS = [
  { label: "Visa",         Logo: VisaLogo,        bg: "white" },
  { label: "Mastercard",   Logo: MastercardLogo,  bg: "white" },
  { label: "PayPal",       Logo: PayPalLogo,      bg: "white" },
  { label: "Orange Money", Logo: OrangeMoneyLogo, bg: "transparent" },
  { label: "Wave",         Logo: WaveLogo,        bg: "transparent" },
  { label: "MTN MoMo",     Logo: MtnMomoLogo,     bg: "transparent" },
];

function PaymentBadges() {
  return (
    <div style={{
      marginTop: "12px",
      paddingTop: "12px",
      borderTop: "1px solid rgba(255,255,255,0.1)",
    }}>
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        justifyContent: "center",
        alignItems: "center",
      }}>
        {PAYMENT_METHODS.map(({ label, Logo, bg }) => (
          <span
            key={label}
            title={label}
            aria-label={label}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: bg,
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "6px",
              padding: "6px 10px",
              height: "36px",
              boxSizing: "border-box",
            }}
          >
            <Logo />
          </span>
        ))}
      </div>
      <p style={{
        marginTop: "10px",
        textAlign: "center",
        fontSize: "11px",
        color: colors.textDim,
      }}>
        🔒 Paiement sécurisé · Sans engagement
      </p>
    </div>
  );
}
